import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LocalDate } from "@/components/ui/local-date";
import { AGENT_AVATAR_BY_SLUG } from "@/lib/agent-avatars";
import { formatTokens, formatUsd } from "@/lib/agents";
import { api } from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents` — admin-only. The AI workforce: one card per agent with its
 * portrait, what it is for, what it burns and the switch to pause it.
 *
 * The whole card opens the agent, the way a project card does — a link
 * on the name alone makes people aim at four words when the obvious
 * target is the card. The pause button is therefore a sibling of the
 * link rather than inside it: a button nested in an anchor is invalid
 * markup and would navigate on click.
 *
 * E2E behaviour contract lives in `e2e/agents.spec.ts`.
 */

type AgentCard = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "paused";
  lastRunAt: string | null;
  today: { tokens: number; costUsd: number };
  month: { tokens: number; costUsd: number };
  pendingSuggestions: number;
  billing: "metered" | "subscription";
  limitedUntil: string | null;
  accessError: string | null;
  working: Array<{
    workItemId: string;
    globalId: number;
    title: string;
    projectName: string;
    userName: string | null;
    detail: string | null;
    startedAt: string;
  }>;
};

/** Heartbeats age out without an event; a crashed agent never says it left. */
const STALE_RELOAD_MS = 60_000;

export const Route = createFileRoute("/_app/agents/")({
  component: AgentsPage,
});

function AgentsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [agents, setAgents] = useState<AgentCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user.globalRole === "admin";

  const load = useCallback(
    () =>
      api
        .get<{ agents: AgentCard[] }>("/api/agents")
        .then((r) => setAgents(r.agents))
        .catch(() => setError("Couldn't load the agents.")),
    [],
  );

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    const socket = getSocket();
    let timer: ReturnType<typeof setTimeout> | null = null;
    // An agent starting or leaving a ticket is announced as a work item change.
    const onChange = (payload: { events?: Array<{ model?: string }> }) => {
      if (!payload?.events?.some((e) => e.model === "workItem")) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 500);
    };
    socket.on("change", onChange);
    const interval = setInterval(() => void load(), STALE_RELOAD_MS);
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(interval);
      socket.off("change", onChange);
    };
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/" />;

  async function setStatus(slug: string, status: "active" | "paused") {
    setAgents((prev) => prev?.map((a) => (a.slug === slug ? { ...a, status } : a)) ?? null);
    await api.patch(`/api/agents/${encodeURIComponent(slug)}`, { status }).catch(() => void load());
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="font-bold text-3xl">Agents</h1>
        <p className="mt-1 text-muted-foreground">
          The AI workforce: what each agent is doing, what it burns, and the switch to pause it.
        </p>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {agents?.map((a) => (
          <AgentTile
            key={a.slug}
            agent={a}
            onToggle={() => setStatus(a.slug, a.status === "active" ? "paused" : "active")}
          />
        ))}
      </div>

      {agents && agents.length === 0 && (
        <p className="text-muted-foreground text-sm">No agents registered.</p>
      )}
    </div>
  );
}

function AgentTile({ agent: a, onToggle }: { agent: AgentCard; onToggle: () => void }) {
  return (
    // `h-full` on both: the grid stretches the wrapper, and without it
    // on the link the card's border would stop at its own content and
    // the row would look ragged.
    <div
      className="relative flex h-full flex-col overflow-hidden rounded-lg border bg-card"
      data-testid="agent-card"
    >
      <Link
        to="/agents/$slug"
        params={{ slug: a.slug }}
        className="block flex-1 p-5 transition-colors hover:bg-accent/30"
        data-testid="agent-open"
      >
        <div className="flex items-start gap-4">
          <Avatar src={AGENT_AVATAR_BY_SLUG[a.slug]} name={a.name} size={88} />
          <div className="min-w-0 flex-1">
            {/* Right padding leaves the pause button its corner. */}
            <h2 className="pr-20 font-bold font-heading text-xl leading-snug">{a.name}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Badge
                variant={a.status === "active" ? "default" : "secondary"}
                data-testid="agent-status"
              >
                {a.status === "active" ? "Active" : "Paused"}
              </Badge>
              {a.limitedUntil && (
                <Badge variant="secondary" data-testid="agent-limited">
                  Waiting for limit
                </Badge>
              )}
              {a.accessError && (
                <Badge variant="destructive" data-testid="agent-access-error">
                  Token refused
                </Badge>
              )}
              {a.pendingSuggestions > 0 && (
                <Badge variant="warning" data-testid="agent-pending">
                  {a.pendingSuggestions} pending
                </Badge>
              )}
            </div>
          </div>
        </div>

        {a.description && (
          <p className="mt-3 line-clamp-3 text-muted-foreground text-sm">{a.description}</p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-today">
              {formatTokens(a.today.tokens)} tok · {formatUsd(a.today.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">today</div>
          </div>
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-month">
              {formatTokens(a.month.tokens)} tok · {formatUsd(a.month.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">
              this month
              {a.billing === "subscription" && (
                <span data-testid="agent-billing"> · estimate, subscription</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 text-muted-foreground text-xs">
          Last activity: {a.lastRunAt ? <LocalDate value={a.lastRunAt} withTime /> : "never"}
        </div>
      </Link>

      {/* Outside the card's link: a ticket link nested in it would be invalid markup. */}
      {a.working.length > 0 && (
        <div className="space-y-1.5 border-t px-5 py-3" data-testid="agent-working">
          <div className="text-muted-foreground text-xs">Working on now</div>
          {a.working.map((w) => (
            <Link
              key={w.workItemId}
              to="/work-item/$id"
              params={{ id: String(w.globalId) }}
              className="block rounded-sm text-sm hover:underline"
              data-testid="agent-working-ticket"
            >
              <span className="font-medium">#{w.globalId}</span> {w.title}
              <span className="block text-muted-foreground text-xs">
                {w.projectName}
                {w.userName ? ` · with ${w.userName}` : ""}
                {w.detail ? ` · ${w.detail}` : ""} · since{" "}
                <LocalDate value={w.startedAt} withTime />
              </span>
            </Link>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="absolute top-4 right-4"
        onClick={onToggle}
        data-testid="agent-toggle"
      >
        {a.status === "active" ? "Pause" : "Resume"}
      </Button>
    </div>
  );
}
